/**
 * D17b: the INTERNAL team/driver alert, relocated OFF the quote action row
 * and renamed 'Alert the team' (DESIGN.md §5.3 flagged proposal, activated —
 * the paired whatsapp-preview.spec edit lands with this component). Quiet
 * section: every send says WHO receives it (Helen-test rule 5). The preview
 * flow is preserved: 'Preview message' → server-built text in the
 * 'Message (editable)' textarea (label VERBATIM) → 'Send the alert'.
 * Name/phone come from the shared capture fields / the store — this panel
 * never duplicates the 'Customer name'/'Customer phone' labels (strict-mode:
 * one instance per screen). Backend notify paths untouched.
 */
import { useState } from 'preact/hooks';
import { WA_NOT_CONFIGURED_COPY, ALERT_FAILED_COPY, type TeamAlertOutcome } from '../lib/actions.js';
import { Banner } from './Banner.js';

export type TeamAlertProps = {
  /** `blocked` (T1949, ask 824) = the SERVER refused to compose, so there is
   *  nothing sendable. Optional, so every existing `ok: true` caller and test
   *  double is unaffected. */
  onPreview: () => Promise<
    { ok: true; message: string } | { ok: false; message: string; blocked?: true }
  >;
  onSendAlert: (body: string) => Promise<TeamAlertOutcome>;
};

export function TeamAlert({ onPreview, onSendAlert }: TeamAlertProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'not-configured' | 'failed'>('idle');
  // A partial fan-out failure is still a failure, but Helen needs to know it
  // was partial: "1 of 2 did not get it" tells her to ring one person, where a
  // bare "it did not send" tells her to re-send to everybody.
  const [partial, setPartial] = useState<{ failed: number; total: number } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const preview = async () => {
    setPreviewError(null);
    const res = await onPreview();
    if (res.ok) setMessage(res.message);
    else {
      setPreviewError(res.message);
      /**
       * ⛔ T1949 (board ask 824) — DROP THE COMPOSED MESSAGE WHEN THE SERVER
       * REFUSED TO COMPOSE ONE. Without this, a preview taken while the band
       * and the count agreed leaves its text in the box; correcting the count
       * so they DISAGREE then refuses the re-preview while the old message -
       * carrying the OLD band beside the new count's job - sits there with Send
       * still enabled. `handleNotifySend` takes a pre-composed string and
       * cannot compare, so this is the only place that can stop it.
       *
       * ⚠️ SCOPED TO `blocked`, deliberately. Clearing on EVERY failure would
       * throw away her edits whenever the network blinked. The wider
       * stale-message case is real and is filed in docs/test-gaps.md.
       */
      if (res.blocked) setMessage(null);
    }
  };

  const send = async () => {
    if (message === null || state === 'busy') return;
    setState('busy');
    setPartial(null);
    const res = await onSendAlert(message);
    if (!res.ok && res.failed != null && res.total != null && res.failed < res.total) {
      setPartial({ failed: res.failed, total: res.total });
    }
    setState(res.ok ? 'sent' : res.notConfigured ? 'not-configured' : 'failed');
  };

  return (
    <div class="team-alert">
      <hr class="section-rule" />
      {!open && (
        <>
          <button type="button" class="btn btn--quiet" onClick={() => setOpen(true)}>
            Alert the team
          </button>
          <p class="recipient-copy">Goes to the team. Not the customer.</p>
        </>
      )}
      {open && (
        <div class="team-alert-panel">
          <h2 class="field-heading">Alert the team</h2>
          <p class="recipient-copy">Goes to the team. Not the customer.</p>
          <div class="action-row">
            <button type="button" class="btn btn--quiet" onClick={() => void preview()}>
              Preview message
            </button>
          </div>
          {previewError && <Banner kind="error">{previewError}</Banner>}
          {message !== null && (
            <>
              <label class="field-label" for="team-message">Message (editable)</label>
              <textarea
                id="team-message"
                class="team-message"
                rows={8}
                value={message}
                onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
              />
              <div class="action-row">
                <button
                  type="button"
                  class="btn btn--quiet"
                  disabled={state === 'busy'}
                  onClick={() => void send()}
                >
                  Send the alert
                </button>
              </div>
            </>
          )}
          {state === 'sent' && <Banner kind="ok">Alert sent to the team.</Banner>}
          {state === 'not-configured' && <Banner kind="info">{WA_NOT_CONFIGURED_COPY}</Banner>}
          {state === 'failed' && (
            <Banner kind="error">
              {partial
                ? `${partial.failed} of ${partial.total} did not get the alert. Ring them.`
                : ALERT_FAILED_COPY}
            </Banner>
          )}
        </div>
      )}
    </div>
  );
}
